import React, { useEffect, useState } from "react";
import { manchiApi } from "@/lib/manchiApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Vault, Lock, Trash2, Plus, KeyRound, Save, ShieldCheck, Sunrise, BookOpen, Newspaper } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function ManchiVault() {
  const [catalog, setCatalog] = useState([]);
  const [subs, setSubs] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newSub, setNewSub] = useState({ slug: "", username: "", password: "" });
  const [addingSub, setAddingSub] = useState(false);

  const [tone, setTone] = useState("");
  const [language, setLanguage] = useState("");
  const [refine, setRefine] = useState("");
  const [autoDraft, setAutoDraft] = useState(false);
  const [autoHour, setAutoHour] = useState(6);
  const [savingCfg, setSavingCfg] = useState(false);

  const [substackUrl, setSubstackUrl] = useState("");
  const [substackEnabled, setSubstackEnabled] = useState(false);
  const [substackDays, setSubstackDays] = useState(7);
  const [reminderEmail, setReminderEmail] = useState("");
  const [reminderViaEmail, setReminderViaEmail] = useState(true);
  const [dailyCategories, setDailyCategories] = useState("");
  const [dailyRegion, setDailyRegion] = useState("Global");
  const [savingExtras, setSavingExtras] = useState(false);

  const [pk, setPk] = useState({ current: "", next: "" });
  const [rotating, setRotating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, subsR, c] = await Promise.all([
        manchiApi.get("/sources"), manchiApi.get("/subscriptions"), manchiApi.get("/config"),
      ]);
      setCatalog(s.data.catalog || []);
      setSubs(subsR.data || []);
      setCfg(c.data);
      setTone(c.data.tone || "");
      setLanguage(c.data.language || "English");
      setRefine(c.data.refine_prompt || "");
      setAutoDraft(!!c.data.auto_draft_enabled);
      setAutoHour(typeof c.data.auto_draft_hour === "number" ? c.data.auto_draft_hour : 6);
      setSubstackUrl(c.data.substack_url || "");
      setSubstackEnabled(!!c.data.substack_reminder_enabled);
      setSubstackDays(c.data.substack_reminder_days || 7);
      setReminderEmail(c.data.reminder_email || "");
      setReminderViaEmail(c.data.reminder_via_email !== false);
      setDailyCategories((c.data.daily_categories || []).join(", "));
      setDailyRegion(c.data.daily_region || "Global");
    } catch { toast.error("Couldn't load vault"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addSub = async (e) => {
    e.preventDefault();
    if (!newSub.slug || !newSub.username || !newSub.password) { toast.error("Fill all fields"); return; }
    setAddingSub(true);
    try {
      await manchiApi.post("/subscriptions", newSub);
      setNewSub({ slug: "", username: "", password: "" });
      toast.success("Credentials vaulted 🔒");
      const { data } = await manchiApi.get("/subscriptions");
      setSubs(data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save"); }
    finally { setAddingSub(false); }
  };

  const delSub = async (id) => {
    try { await manchiApi.delete(`/subscriptions/${id}`); setSubs((l) => l.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch { toast.error("Couldn't remove"); }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await manchiApi.patch("/config", {
        tone, language, refine_prompt: refine,
        auto_draft_enabled: autoDraft, auto_draft_hour: Number(autoHour),
      });
      toast.success("Defaults saved");
    }
    catch { toast.error("Couldn't save"); }
    finally { setSavingCfg(false); }
  };

  const saveExtras = async () => {
    setSavingExtras(true);
    try {
      await manchiApi.patch("/config", {
        substack_url: substackUrl,
        substack_reminder_enabled: substackEnabled,
        substack_reminder_days: Number(substackDays),
        reminder_email: reminderEmail,
        reminder_via_email: reminderViaEmail,
        daily_categories: dailyCategories.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean),
        daily_region: dailyRegion,
      });
      toast.success("Saved");
    } catch { toast.error("Couldn't save"); }
    finally { setSavingExtras(false); }
  };

  const rotate = async (e) => {
    e.preventDefault();
    setRotating(true);
    try {
      await manchiApi.post("/rotate-passkey", { current_passkey: pk.current, new_passkey: pk.next });
      setPk({ current: "", next: "" });
      toast.success("Passcode updated 🔑");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't update"); }
    finally { setRotating(false); }
  };

  if (loading) return <div className="text-center py-16 text-rose-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  const usedSlugs = new Set(subs.map((s) => s.slug));
  const available = catalog.filter((c) => !usedSlugs.has(c.slug));

  return (
    <div className="space-y-8" data-testid="manchi-vault">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.25em] text-rose-400 flex items-center gap-2">
          <Vault className="w-4 h-4" /> private vault
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-rose-900 mt-1">Subscriptions &amp; settings.</h1>
      </div>

      {/* Subscription credentials */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6">
        <div className="font-bold text-rose-900 flex items-center gap-2 mb-1"><Lock className="w-4 h-4 text-pink-500" /> Newspaper subscriptions</div>
        <p className="text-rose-400 text-xs mb-4">Stored encrypted at rest. Paywall fetch is best-effort (public headlines) for now.</p>

        <div className="space-y-2 mb-4">
          {subs.length === 0 && <p className="text-rose-400 text-sm">No subscriptions saved yet.</p>}
          {subs.map((s) => (
            <div key={s.id} data-testid={`manchi-sub-${s.slug}`} className="flex items-center justify-between rounded-2xl border border-pink-100 bg-pink-50/40 px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-black" style={{ background: s.meta?.tint || "#ec4899" }}>
                  {(s.meta?.name || s.slug).slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-rose-900 text-sm">{s.meta?.name || s.slug}</div>
                  <div className="text-rose-400 text-xs truncate">{s.username}</div>
                </div>
              </div>
              <button onClick={() => delSub(s.id)} data-testid={`manchi-sub-delete-${s.slug}`} className="text-rose-300 hover:text-rose-600 p-2">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <form onSubmit={addSub} className="grid sm:grid-cols-[160px_1fr_1fr_auto] gap-2">
          <select value={newSub.slug} onChange={(e) => setNewSub({ ...newSub, slug: e.target.value })}
            data-testid="manchi-sub-source-select"
            className="rounded-xl border border-pink-200 bg-pink-50/40 px-3 h-10 text-sm text-rose-800 focus:outline-none focus:ring-2 focus:ring-pink-400">
            <option value="">Choose paper…</option>
            {available.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <Input value={newSub.username} onChange={(e) => setNewSub({ ...newSub, username: e.target.value })}
            placeholder="Username / email" data-testid="manchi-sub-username" className="rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 h-10" />
          <Input type="password" value={newSub.password} onChange={(e) => setNewSub({ ...newSub, password: e.target.value })}
            placeholder="Password" data-testid="manchi-sub-password" className="rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 h-10" />
          <Button type="submit" disabled={addingSub} data-testid="manchi-sub-add-btn"
            className="rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white h-10">
            {addingSub ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </form>
      </div>

      {/* Default voice */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6 space-y-4">
        <div className="font-bold text-rose-900 flex items-center gap-2"><Save className="w-4 h-4 text-pink-500" /> Default writing style</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-rose-400">Tone</label>
            <Textarea value={tone} onChange={(e) => setTone(e.target.value)} rows={2} data-testid="manchi-cfg-tone" className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 resize-none" />
          </div>
          <div>
            <label className="text-xs font-semibold text-rose-400">Language</label>
            <Input value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="manchi-cfg-language" className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-rose-400">Default refinement prompt</label>
          <Textarea value={refine} onChange={(e) => setRefine(e.target.value)} rows={2} data-testid="manchi-cfg-refine"
            className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 resize-none" />
        </div>

        <div className="rounded-2xl border border-pink-100 bg-pink-50/40 p-4 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-300 to-pink-400 flex items-center justify-center flex-shrink-0">
              <Sunrise className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-semibold text-rose-900 text-sm">Morning auto-draft</div>
              <div className="text-rose-400 text-xs">A fresh draft is waiting each morning for you to review.</div>
              {autoDraft && (
                <div className="flex items-center gap-1.5 mt-2 text-xs text-rose-500">
                  at
                  <input type="number" min="0" max="23" value={autoHour}
                    onChange={(e) => setAutoHour(e.target.value)} data-testid="manchi-autodraft-hour"
                    className="w-14 rounded-lg border border-pink-200 bg-white px-2 py-1 text-rose-800 focus:outline-none focus:ring-2 focus:ring-pink-400" />
                  :00 (UK time)
                </div>
              )}
            </div>
          </div>
          <Switch checked={autoDraft} onCheckedChange={setAutoDraft} data-testid="manchi-autodraft-toggle"
            className="data-[state=checked]:bg-pink-500" />
        </div>

        <Button onClick={saveCfg} disabled={savingCfg} data-testid="manchi-cfg-save"
          className="rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white">
          {savingCfg ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save defaults
        </Button>
      </div>

      {/* Substack */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6 space-y-4" data-testid="manchi-substack-settings">
        <div className="flex items-center justify-between">
          <div className="font-bold text-rose-900 flex items-center gap-2"><BookOpen className="w-4 h-4 text-pink-500" /> Substack blog</div>
          <Switch checked={substackEnabled} onCheckedChange={setSubstackEnabled} data-testid="manchi-substack-toggle" className="data-[state=checked]:bg-pink-500" />
        </div>
        <p className="text-rose-400 text-xs -mt-2">Substack has no auto-publish API, so ManchiGirl reminds you (and one-click opens a new Substack post with your draft copied).</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-rose-400">Your Substack URL</label>
            <Input value={substackUrl} onChange={(e) => setSubstackUrl(e.target.value)} data-testid="manchi-substack-url"
              placeholder="yourname.substack.com" className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-rose-400">Remind me every (days)</label>
            <Input type="number" min="1" max="90" value={substackDays} onChange={(e) => setSubstackDays(e.target.value)} data-testid="manchi-substack-days"
              className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-rose-400">Reminder email</label>
            <Input value={reminderEmail} onChange={(e) => setReminderEmail(e.target.value)} data-testid="manchi-reminder-email"
              placeholder="you@email.com" className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40" />
          </div>
          <label className="flex items-center gap-2 mt-6 text-sm text-rose-600">
            <input type="checkbox" checked={reminderViaEmail} onChange={(e) => setReminderViaEmail(e.target.checked)} data-testid="manchi-reminder-email-toggle" className="accent-pink-500 w-4 h-4" />
            Email me the reminder
          </label>
        </div>
      </div>

      {/* Daily Brief settings */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6 space-y-4" data-testid="manchi-daily-settings">
        <div className="font-bold text-rose-900 flex items-center gap-2"><Newspaper className="w-4 h-4 text-pink-500" /> Daily Brief topics</div>
        <div>
          <label className="text-xs font-semibold text-rose-400">Categories (comma-separated)</label>
          <Textarea value={dailyCategories} onChange={(e) => setDailyCategories(e.target.value)} rows={2} data-testid="manchi-daily-categories"
            placeholder="World, Politics, Business & Economy, Technology, Science, International Relations"
            className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 resize-none" />
        </div>
        <div>
          <label className="text-xs font-semibold text-rose-400">Region focus</label>
          <Input value={dailyRegion} onChange={(e) => setDailyRegion(e.target.value)} data-testid="manchi-daily-region"
            placeholder="Global (or UK, India…)" className="mt-1 rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40" />
        </div>
        <Button onClick={saveExtras} disabled={savingExtras} data-testid="manchi-extras-save"
          className="rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white">
          {savingExtras ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save blog &amp; brief settings
        </Button>
      </div>

      {/* Passcode */}
      <div className="bg-white rounded-3xl border border-pink-100 shadow-lg shadow-pink-200/30 p-6">
        <div className="font-bold text-rose-900 flex items-center gap-2 mb-1"><KeyRound className="w-4 h-4 text-pink-500" /> Change passcode</div>
        <p className="text-rose-400 text-xs mb-4 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Min 6 characters. You&apos;ll stay signed in.</p>
        <form onSubmit={rotate} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
          <Input type="password" value={pk.current} onChange={(e) => setPk({ ...pk, current: e.target.value })}
            placeholder="Current passcode" data-testid="manchi-pk-current" className="rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 h-10" />
          <Input type="password" value={pk.next} onChange={(e) => setPk({ ...pk, next: e.target.value })}
            placeholder="New passcode" data-testid="manchi-pk-new" className="rounded-xl border-pink-200 focus-visible:ring-pink-400 bg-pink-50/40 h-10" />
          <Button type="submit" disabled={rotating} data-testid="manchi-pk-btn"
            className="rounded-xl bg-rose-500 hover:bg-rose-600 text-white h-10">
            {rotating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}
          </Button>
        </form>
      </div>
    </div>
  );
}
